import { type IDisposable, type Scene, Vector3 } from '@babylonjs/core';

import { CameraTargetSystem } from '../ecs';
import { RenderManager } from '../manager';

import { CameraAnimationOption, type CameraInputParam, CameraPresetTypes, CameraType } from './data';
import {
  DeviceOrientationRotation,
  DoubleTouchPinchFOV,
  SingleTouchFOV,
  SingleTouchRotation,
  SingleTouchVerticalMove,
} from './inputs';
import { ZEDCamera } from './ZEDCamera';
import { createZEDCamera, toRadian } from './ZEDCameraHelper';
import { getKakaoMapStyleInputs } from './ZEDCameraInputHelper';

export class ZEDCameraAPI implements IDisposable {
  public camera: ZEDCamera;
  protected inputs?: CameraInputParam;
  protected cameraType?: CameraType;

  constructor(
    protected scene: Scene,
    defaultInput: boolean = true,
    protected renderManager: RenderManager,
    protected cameraTargetSystem: CameraTargetSystem
  ) {
    this.camera = createZEDCamera(this.scene, this.renderManager);
    if (defaultInput) this.applyPresetInputs('Default');
  }
  public dispose() {
    this.camera.dispose();
    this.storedInputs = undefined;
  }

  //----------------------------------------
  // Enable
  //----------------------------------------
  /**
   * 현재 활성화 여부를 반환합니다.
   * @returns 현재 활성화 여부
   */
  public getEnableControl() {
    return this.camera.getEnableControl();
  }
  /**
   * 활성화 여부에 따라 인터페이스를 해제/등록 합니다
   * @param enable 활성화 여부
   */
  public enableControl(enable: boolean) {
    this.camera.enableControl(enable);
  }

  //----------------------------------------
  // Store State
  //----------------------------------------
  protected storedInputs?: CameraInputParam | undefined;
  /** 현재 카메라 상태를 저장합니다 */
  public storeCameraState() {
    this.camera.storeState();
    this.storedInputs = this.inputs;
  }
  /** 저장했던 상태로 원복합니다 */
  public restoreCameraState() {
    this.camera.restoreStateValuesExceptTransform();
    this.restoreInputs();
  }
  /** 저장했던 인터페이스로 원복합니다 */
  protected restoreInputs() {
    if (this.storedInputs !== undefined) {
      this.attachInputs(this.storedInputs);
    }
  }

  //----------------------------------------
  // Attach Inputs
  //----------------------------------------
  /**
   * 카메라 인터페이스를 대체합니다
   * @param param 인터페이스 매개변수 inputType이 Custom이면, 입력한 inputs로 대체하고 아니면, 사전에 정의된 인터페이스를 등록합니다.
   * @returns
   */
  public attachInputs(inputs: CameraInputParam | undefined) {    
    if (!inputs || (!inputs.desktop && !inputs.mobile)) {
      console.log('inputs 입력이 필요합니다.');
      return;
    }
  
    this.camera.attachInputs(inputs, false);

    this.inputs = inputs;
  }
  /**
   * cameraType에 따른 사전에 정의된 인터페이스를 반환합니다
   * @param param CameraType
   * @returns CameraInputParam
   */
  protected getPresetInputs(cameraType: CameraType) {
    switch (cameraType) {
      case 'Default':
        return getKakaoMapStyleInputs();
      case 'AR':
        SingleTouchRotation.option = {
          mouseButton: 'all',
          mouseKeyCode: 'none',
          alpha: true,
          beta: false,
          inertia: 0,
          sensibilityX: 1000,
          sensibilityY: 1000,
        };
        SingleTouchVerticalMove.option = { mouseButton: 'all', mouseKeyCode: 'none' };
        SingleTouchFOV.option = { mouseButton: 'all', mouseKeyCode: 'ctrlKey' };

        return {
          desktop: [SingleTouchVerticalMove, SingleTouchRotation, SingleTouchFOV],
          mobile: [DeviceOrientationRotation, SingleTouchVerticalMove, DoubleTouchPinchFOV],
        } as CameraInputParam;
      default:
        return getKakaoMapStyleInputs();
    }
  }
  public applyPresetInputs(cameraType: CameraType) {
    if (this.cameraType === cameraType) return;

    this.cameraType = cameraType;
    this.attachInputs(this.getPresetInputs(cameraType));
  }

  /**
   * 현재 인터페이스의 타입을 반환합니다
   * @returns 현재 인터페이스의 타입
   */
  public getCameraType() {
    return this.cameraType?.toString();
  }

  public getPresetCameraTypes() {
    return CameraPresetTypes;
  }

  //----------------------------------------
  // Immediate Move
  //----------------------------------------
  public setPosition(...args: Parameters<typeof this.camera.setPosition>) {
    this.camera.setPosition(...args);
  }
  public setRotation(...args: Parameters<typeof this.camera.setRotation>) {
    this.camera.setRotation(...args);
  }
  public setTransform(position: Vector3, ...args: Parameters<typeof this.camera.setRotation>) {
    const [rotation, isRelative, isRadian, isLeftHandCoordinate, render] = args;
    this.camera.setPosition(position, isRelative, isLeftHandCoordinate, false);
    this.camera.setRotation(rotation, isRelative, isRadian, isLeftHandCoordinate, render);
  }
  //----------------------------------------
  // MoveTo
  //----------------------------------------
  /**
   * CameraTarget Component가 부착된 Entities를 반환합니다
   * @returns CameraTargetResponse
   */
  public getCameraTargets() {
    return this.cameraTargetSystem.getCameraTargets();
  }
  /**
   * 입력한 entityId에 매칭되는 CameraTarget을 바라보는 각도/위치로 이동합니다
   * @param entityId CameraTarget Entity ID
   * @param distance Target과의 거리 (fitDistance 활성화 시 무시)
   * @param fitDistance Target과의 거리를 Target의 크기에 알맞게 맞출 지 여부
   * @param fitMargin fitDistance일 시 Target 크기에 비율로 거리를 조정하는 값
   * @param animationOption CameraAnimationOption
   * @returns entityId에 매칭되는 타겟이 있어 애니메이션에 성공하면 true, 실패하면 false
   */
  public async moveToTargetEntity(
    entityId: string,
    distance: number,
    fitDistance: boolean = false,
    fitMargin: number = 1,
    animationOption?: Partial<CameraAnimationOption>
  ) {
    const entity = this.cameraTargetSystem.getCameraTargetEntity(entityId);
    if (entity) {
      await this.camera.moveToTarget(entity.TransformNode, distance, fitDistance, fitMargin, animationOption);
      return true;
    } else {
      console.log('존재하지 않는 Entity 입니다.');
      return false;
    }
  }
  /**
   * 입력한 targetName에 매칭되는 Target을 바라보는 각도/위치로 이동합니다
   * @param targetName CameraTarget 이름 (scene에서 탐색)
   * @param distance Target과의 거리 (fitDistance 활성화 시 무시)
   * @param fitDistance Target과의 거리를 Target의 크기에 알맞게 맞출 지 여부
   * @param fitMargin fitDistance일 시 Target 크기에 비율로 거리를 조정하는 값
   * @param animationOption CameraAnimationOption
   * @returns targetName에 매칭되는 타겟이 있어 애니메이션에 성공하면 true, 실패하면 false
   */
  public async moveToTargetName(
    targetName: string,
    distance: number,
    fitDistance: boolean = false,
    fitMargin: number = 1,
    animationOption?: Partial<CameraAnimationOption>
  ) {
    const target = this.scene.transformNodes.find((t) => t.name === targetName);
    if (target) {
      await this.camera.moveToTarget(target, distance, fitDistance, fitMargin, animationOption);
      return true;
    } else {
      console.log('존재하지 않는 이름 입니다.');
      return false;
    }
  }
  /**
   * 입력한 좌표/각도로 이동합니다
   * @param position 좌표
   * @param rotation 각도
   * @param animationOption CameraAnimationOption
   * @param isRelative true일 시 입력한 position, rotation으로 상대적으로 이동합니다
   * @param isRadian true일 시 입력한 rotation을 radian으로 계산하고 false일 시 radian으로 변환합니다
   * @param isLeftHandCoordinate true일 시 입력한 position을 babylonjs 기존 오른손 좌표계에서 왼손 좌표계로 변환합니다 (unity 왼손 좌표계)
   */
  public async moveToCoordinate(
    position: Vector3,
    rotation: Vector3 = Vector3.Zero(),
    animationOption?: Partial<CameraAnimationOption>,
    isRelative: boolean = false,
    isRadian: boolean = false,
    isLeftHandCoordinate: boolean = true
  ): Promise<void> {
    await this.camera.moveToCoordinate(position, rotation, animationOption, isRelative, isRadian, isLeftHandCoordinate);
  }

  public async moveTo(
    target?: Vector3,
    alpha?: number,
    beta?: number,
    radius?: number,
    animationOption?: Partial<CameraAnimationOption>
  ) {
    await this.camera.moveTo(target, alpha, beta, radius, animationOption);
  }

  //----------------------------------------
  // FitTo
  //----------------------------------------
  /**
   * 카메라를 특정 좌표 기준 영역이 화면에 들어오도록 TopView로 맞춥니다
   * @param animationOption CameraAnimationOption
   * @param fitMargin 영역에 비율로 거리를 조정하는 값
   * @param rotateable true일 시 주어진 영역의 크기가 가로보다 세로가 길다면, 회전합니다
   * @param pivotPosition 영역 기준 좌표 (default : Vector3.Zero)
   * @param overrideBounds 영역 크기 (default : Scene.Extends or Floorplan.Extends)
   */
  public async fitToTopView(
    animationOption?: Partial<CameraAnimationOption>,
    fitMargin: number = 1.2,
    rotateable: boolean = false,
    pivotPosition: Vector3 = Vector3.Zero(),
    overrideBounds?: { min: Vector3; max: Vector3 }
  ) {
    await this.camera.fitToTopView(animationOption, fitMargin, rotateable, pivotPosition, overrideBounds);
  }
  /**
   * 카메라를 특정 좌표 기준 영역이 화면에 들어오도록 QuaterView로 맞춥니다
   * @param animationOption CameraAnimationOption
   * @param fitMargin 영역에 비율로 거리를 조정하는 값
   * @param pivotPosition 영역 기준 좌표 (default : Vector3.Zero)
   * @param overrideBounds 영역 크기 (default : Scene.Extends or Floorplan.Extends)
   */
  public async fitToQuaterView(
    animationOption?: Partial<CameraAnimationOption>,
    fitMargin: number = 1.2,
    pivotPosition: Vector3 = Vector3.Zero(),
    overrideBounds?: { min: Vector3; max: Vector3 }
  ) {
    await this.camera.fitToQuaterView(animationOption, fitMargin, pivotPosition, overrideBounds);
  }

  //----------------------------------------
  // Viewport
  //----------------------------------------
  /**
   * 입력한 viewport로 변경합니다 (animationOption을 입력하면, 애니메이션으로 변경합니다)
   * @param viewport 변경할 viewport
   * @param animationOption CameraAnimationOption (입력할 시 애니메이션 출력)
   */
  public async setViewport(
    viewport: Partial<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>,
    animationOption?: Partial<CameraAnimationOption>
  ) {
    await this.camera.setViewport(viewport, animationOption);
  }

  //----------------------------------------
  // ARCamera
  //----------------------------------------
  /**
   * AR 카메라를 활성화 합니다 (AR 카메라 사용 시 이 함수 호출이 권장됩니다)
   * 이 함수는 버튼을 통해 호출해야 DeviceOrientation(자이로) 권한을 획득할 수 있습니다.
   * @param moveToCoordinateParams 입력 시 해당 매개변수로 moveToCoordinate 이동합니다 (입력은 배열로 입력해야합니다)
   */
  public async enableARCamera(moveToCoordinateParams?: Parameters<this['moveToCoordinate']>) {
    this.storeCameraState();
    this.applyPresetInputs('AR');

    this.camera.fov = 120 * toRadian;
    this.camera.upperBetaLimit = 180 * toRadian;

    if (moveToCoordinateParams) {
      const [position, rotation, animationOption, isRelative, isRadian, isLeftHandCoordinate] = moveToCoordinateParams;

      await this.moveToCoordinate(position, rotation, animationOption, isRelative, isRadian, isLeftHandCoordinate);
    }
  }
  /**
   * AR 카메라를 비활성화 합니다 (AR 카메라 사용 종료 시 이 함수 호출이 권장됩니다)
   * @param fitToTopViewParam 입력 시 해당 매개변수로 fitToTopView 이동합니다 (입력은 배열로 입력해야합니다)
   */
  public async disableARCamera(fitToTopViewParam?: Parameters<this['fitToTopView']>) {
    this.restoreCameraState();
    if (fitToTopViewParam) {
      const [animationOption, fitMargin, rotateable, pivotPosition, overrideBounds] = fitToTopViewParam;
      await this.fitToTopView(animationOption, fitMargin, rotateable, pivotPosition, overrideBounds);
    }
  }

  //----------------------------------------
  // Bind
  //----------------------------------------
  /**
   * 등록 시 fitTo~ 관련 함수에서 defaultExtends로 사용합니다
   * @param getter default로 반환할 getter 함수
   */
  protected bindGetDefaultExtends(...args: Parameters<typeof this.camera.bindGetDefaultExtends>) {
    const [getter] = args;
    this.camera.bindGetDefaultExtends(getter);
  }
}
